import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Briefcase, Calendar, Clock, CheckCircle2, AlertCircle, ExternalLink, 
  Upload, Paperclip, Play, FileText, Send, Sparkles, AlertTriangle, 
  ChevronRight, RefreshCw, Users, User, Download, Check
} from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { SiFigma, SiGoogledrive } from 'react-icons/si';

const STATUS_COLORS = {
  Pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Submitted: 'bg-purple-50 text-purple-700 border-purple-200',
  'Under Review': 'bg-amber-50 text-amber-800 border-amber-300',
  'Changes Requested': 'bg-orange-50 text-orange-900 border-orange-300 font-bold',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  Archived: 'bg-gray-50 text-gray-600 border-gray-200'
};

const PRIORITY_COLORS = {
  High: 'bg-rose-50 text-rose-600 border-rose-200',
  Medium: 'bg-amber-50 text-amber-600 border-amber-200',
  Low: 'bg-blue-50 text-blue-600 border-blue-200'
};

const ROLE_BADGES = {
  Developer: 'bg-indigo-100 text-indigo-700',
  Intern: 'bg-amber-100 text-amber-800',
  Employee: 'bg-blue-100 text-blue-700',
  Designer: 'bg-pink-100 text-pink-700',
  'Team Lead': 'bg-emerald-100 text-emerald-700',
  Manager: 'bg-purple-100 text-purple-700',
  CEO: 'bg-red-100 text-red-700'
};

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(null);

  // Submit task modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submittingTask, setSubmittingTask] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    githubLink: '',
    liveLink: '',
    notes: '',
    updateType: 'Final'
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const getHeaders = () => {
    let token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    if (!token || token === 'null' || token === 'undefined' || token.startsWith('{') || token.startsWith('[')) {
      token = null;
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const res = await axios.get(`${window.API_URL}/api/portal/employee/projects`, { headers });
      const rawData = res.data || [];
      const data = Array.from(new Map(rawData.map(p => [p._id, p])).values());
      setProjects(data);
      if (data.length > 0 && !activeProject) {
        setActiveProject(data[0]);
      } else if (activeProject) {
        const found = data.find(p => p._id === activeProject._id);
        if (found) setActiveProject(found);
      }
    } catch (err) {
      console.error("Failed to load projects:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleStartTask = async (task) => {
    try {
      const headers = getHeaders();
      await axios.put(`${window.API_URL}/api/portal/shared/tasks/${task._id}/start`, {}, { headers });
      Swal.fire({
        icon: 'success',
        title: 'Task Started!',
        text: 'Keep track of your daily progress.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
      fetchProjects();
    } catch (err) {
      Swal.fire('Error', 'Failed to start task.', 'error');
    }
  };

  const openSubmitModal = (task, updateType = 'Final') => {
    setSubmittingTask(task);
    setSubmissionData({
      githubLink: task.githubLink || '',
      liveLink: task.liveLink || '',
      notes: task.submissionNotes || '',
      updateType
    });
    setFiles([]);
    setShowSubmitModal(true);
  };

  const handleSubmitWork = async (e) => {
    e.preventDefault();
    if (!submittingTask) return;

    if (!submissionData.notes.trim()) {
      return Swal.fire('Error', 'Please provide submission notes describing your work.', 'warning');
    }

    setSubmitting(true);
    try {
      const headers = getHeaders();
      const formPayload = new FormData();
      formPayload.append('notes', submissionData.notes);
      formPayload.append('submissionNotes', submissionData.notes);
      if (submissionData.githubLink) formPayload.append('githubLink', submissionData.githubLink);
      if (submissionData.liveLink) formPayload.append('liveLink', submissionData.liveLink);

      const targetStatus = submissionData.updateType === 'Final' ? 'Submitted' : 'In Progress';
      formPayload.append('status', targetStatus);

      if (files && files.length > 0) {
        Array.from(files).forEach(f => {
          formPayload.append('files', f);
        });
      }

      await axios.put(`${window.API_URL}/api/portal/shared/tasks/${submittingTask._id}/submit`, formPayload, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      Swal.fire({
        icon: 'success',
        title: submissionData.updateType === 'Final' ? 'Task Submitted for Review!' : 'Progress Saved!',
        text: `Work updated for "${submittingTask.title}".`,
        timer: 2000,
        showConfirmButton: false
      });

      setShowSubmitModal(false);
      setSubmittingTask(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = (filename) => {
    if (!filename) return '#';
    const bare = filename.startsWith('/uploads/') ? filename.replace('/uploads/', '') : filename.replace(/^\/+/, '');
    return `${window.API_URL}/uploads/${bare}`;
  };

  return (
    <div className="space-y-6 pb-12 font-sans text-gray-900">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-[#f06600]/10 text-[#f06600] rounded-xl">
              <Briefcase className="w-6 h-6" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              My Assigned Client Projects
            </h2>
          </div>
          <p className="text-gray-500 text-sm mt-1 ml-10">
            View client projects you are working on, collaborate with teammates, and submit your daily tasks.
          </p>
        </div>

        <button
          onClick={fetchProjects}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#f06600]' : ''}`} />
          Refresh Projects
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl p-16 text-center border border-gray-200">
          <RefreshCw className="w-8 h-8 text-[#f06600] animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-semibold">Loading your assigned projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white rounded-3xl p-16 text-center border border-gray-200 shadow-sm">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-lg font-bold text-gray-800 mb-1">No Projects Assigned Yet</h4>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            You haven't been assigned to any client projects yet. When your team lead or admin assigns you, it will appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Projects List */}
          <div className="lg:col-span-4 space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
              Your Projects ({projects.length})
            </p>
            {projects.map(proj => {
              const isSelected = activeProject?._id === proj._id;
              const stats = proj.myStats || { totalTasks: 0, completedTasks: 0, progressPercent: 0 };

              return (
                <div
                  key={proj._id}
                  onClick={() => setActiveProject(proj)}
                  className={`p-5 rounded-2xl border cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'bg-white border-[#f06600] shadow-md ring-2 ring-[#f06600]/15'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    {proj.clientName && (
                      <span className="text-[10px] font-extrabold text-[#f06600] bg-[#f06600]/10 px-2 py-0.5 rounded-full uppercase">
                        {proj.clientName}
                      </span>
                    )}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[proj.status] || 'bg-gray-100'}`}>
                      {proj.status}
                    </span>
                  </div>

                  <h3 className="font-extrabold text-base text-gray-900 mb-1">
                    {proj.title}
                  </h3>

                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                    {proj.description || 'No description.'}
                  </p>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[11px] text-gray-600 font-semibold">
                      <span>My Tasks: {stats.completedTasks}/{stats.totalTasks}</span>
                      <span className="text-[#f06600] font-bold">{stats.progressPercent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="bg-[#f06600] h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${stats.progressPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Column: Active Project Details & My Daily Tasks */}
          <div className="lg:col-span-8 space-y-6">
            {activeProject ? (
              <>
                {/* Project Header Banner */}
                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex justify-between items-start flex-wrap gap-4 mb-3">
                    <div>
                      {activeProject.clientName && (
                        <span className="inline-block text-xs font-extrabold text-[#f06600] uppercase tracking-wider mb-1">
                          Client: {activeProject.clientName}
                        </span>
                      )}
                      <h2 className="text-2xl font-extrabold text-gray-900">
                        {activeProject.title}
                      </h2>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_COLORS[activeProject.status] || 'bg-gray-100'}`}>
                        {activeProject.status}
                      </span>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full border ${PRIORITY_COLORS[activeProject.priority] || 'bg-gray-100'}`}>
                        {activeProject.priority} Priority
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4 whitespace-pre-wrap leading-relaxed">
                    {activeProject.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-3 border-t border-gray-100">
                    {activeProject.startDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        Started: <strong className="text-gray-800">{new Date(activeProject.startDate).toLocaleDateString()}</strong>
                      </span>
                    )}
                    {activeProject.deadline && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        Deadline: <strong className="text-gray-800">{new Date(activeProject.deadline).toLocaleDateString()}</strong>
                      </span>
                    )}
                  </div>

                  {/* Teammates on this Project */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-gray-500" />
                      Team Members Collaborating:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(activeProject.assignedEmployees || []).map(member => (
                        <div 
                          key={member._id}
                          className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-xl text-xs font-semibold text-gray-800"
                        >
                          <User className="w-3 h-3 text-gray-400" />
                          <span>{member.fullName}</span>
                          <span className={`text-[10px] px-1.5 py-0.2 rounded font-bold ${ROLE_BADGES[member.role] || 'bg-gray-200'}`}>
                            {member.role || 'Member'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* My Daily Tasks for this Project */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#f06600]" />
                      My Daily Tasks on this Project ({activeProject.myTasks?.length || 0})
                    </h3>
                  </div>

                  {(!activeProject.myTasks || activeProject.myTasks.length === 0) ? (
                    <div className="bg-white rounded-3xl border border-gray-200 p-10 text-center">
                      <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-2 opacity-70" />
                      <h4 className="text-base font-bold text-gray-800 mb-1">No Daily Tasks Assigned Yet</h4>
                      <p className="text-xs text-gray-500 max-w-sm mx-auto">
                        Your manager or admin will assign daily tasks to you under this project. Check back shortly.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activeProject.myTasks.map(task => {
                        const isPending = task.status === 'Pending';
                        const isInProgress = task.status === 'In Progress';
                        const isSubmitted = task.status === 'Submitted' || task.status === 'Under Review';
                        const isCompleted = task.status === 'Completed';
                        const isChangesRequested = task.status === 'Changes Requested';

                        return (
                          <div 
                            key={task._id}
                            className={`bg-white rounded-3xl border transition-all p-5 sm:p-6 ${
                              isChangesRequested
                                ? 'border-orange-300 ring-2 ring-orange-200/60 shadow-sm'
                                : isCompleted
                                ? 'border-emerald-200'
                                : 'border-gray-200 shadow-sm hover:shadow-md'
                            }`}
                          >
                            {/* Badges */}
                            <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${STATUS_COLORS[task.status] || 'bg-gray-100'}`}>
                                  {task.status}
                                </span>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-700">
                                  {task.category || 'General'}
                                </span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${PRIORITY_COLORS[task.priority] || 'bg-gray-100'}`}>
                                  {task.priority} Priority
                                </span>
                              </div>

                              <span className="text-xs text-gray-500 font-medium">
                                Due: <strong className="text-gray-800">{new Date(task.deadline).toLocaleDateString()}</strong>
                              </span>
                            </div>

                            {/* Task Title */}
                            <h4 className="text-base font-extrabold text-gray-900 mb-1">
                              {task.title}
                            </h4>

                            {/* Description */}
                            <p className="text-xs text-gray-600 mb-3 whitespace-pre-wrap leading-relaxed">
                              {task.description}
                            </p>

                            {/* Specs, Reference links, Expected output */}
                            {(task.expectedOutput || task.referenceUrl || task.adminNotes) && (
                              <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 text-xs text-gray-700 space-y-1 mb-3">
                                {task.expectedOutput && (
                                  <p><strong className="text-gray-900">Expected Output:</strong> {task.expectedOutput}</p>
                                )}
                                {task.adminNotes && (
                                  <p><strong className="text-amber-800">Admin Instructions:</strong> {task.adminNotes}</p>
                                )}
                                {task.referenceUrl && (
                                  <p className="truncate">
                                    <strong className="text-gray-900">Reference URL:</strong>{' '}
                                    <a href={task.referenceUrl} target="_blank" rel="noreferrer" className="text-[#f06600] underline font-semibold">
                                      {task.referenceUrl}
                                    </a>
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Attached Files & Images from Admin / Project Manager */}
                            {task.attachments && task.attachments.length > 0 && (
                              <div className="bg-orange-50/60 border border-orange-200/80 rounded-2xl p-3.5 mb-3">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-orange-950 mb-2">
                                  <Paperclip className="w-3.5 h-3.5 text-[#f06600]" />
                                  Project Files &amp; Reference Images ({task.attachments.length}):
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {task.attachments.map((file, idx) => {
                                    const fileUrl = getFileUrl(file);
                                    const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(file);
                                    const displayName = file.split('/').pop().replace(/^\d+[-_]?/, '');
                                    return (
                                      <div key={idx} className="flex items-center justify-between gap-2 p-2.5 bg-white rounded-xl border border-orange-200/70 text-xs shadow-2xs">
                                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                          {isImage ? (
                                            <a href={fileUrl} target="_blank" rel="noreferrer" className="shrink-0 group">
                                              <img
                                                src={fileUrl}
                                                alt="attachment"
                                                className="w-9 h-9 rounded-lg object-cover border border-gray-200 group-hover:scale-105 transition-transform"
                                              />
                                            </a>
                                          ) : (
                                            <div className="p-2 bg-orange-50 text-[#f06600] rounded-lg shrink-0">
                                              <FileText className="w-4 h-4" />
                                            </div>
                                          )}
                                          <div className="min-w-0 flex-1">
                                            <span className="font-bold text-gray-800 truncate block text-[11px]" title={file}>
                                              {displayName}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                              {isImage ? 'Image attachment' : 'Document file'}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            download
                                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#f06600] hover:bg-[#d05800] text-white rounded-lg text-[10px] font-bold transition-colors shadow-2xs"
                                            title="Download file"
                                          >
                                            <Download className="w-3 h-3" /> Download
                                          </a>
                                          <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                            title="Open in new tab"
                                          >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                          </a>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Feedback Banner if Changes Requested */}
                            {isChangesRequested && task.feedback && (
                              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3.5 mb-3 text-xs text-orange-950">
                                <div className="flex items-center gap-1.5 font-bold mb-1 text-orange-800">
                                  <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
                                  Admin Revision Requested:
                                </div>
                                <p className="leading-relaxed font-medium">{task.feedback}</p>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 flex-wrap">
                              {isPending && (
                                <button
                                  type="button"
                                  onClick={() => handleStartTask(task)}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
                                >
                                  <Play className="w-3.5 h-3.5" /> Start Task
                                </button>
                              )}

                              {(isInProgress || isPending || isChangesRequested) && (
                                <button
                                  type="button"
                                  onClick={() => openSubmitModal(task, 'Final')}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-[#f06600]/20"
                                >
                                  <Send className="w-3.5 h-3.5" />
                                  {isChangesRequested ? 'Resubmit Revisions' : 'Submit Daily Work'}
                                </button>
                              )}

                              {isSubmitted && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl text-xs font-bold">
                                  <Sparkles className="w-3.5 h-3.5" /> Submitted (Under Admin Review)
                                </span>
                              )}

                              {isCompleted && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold">
                                  <Check className="w-3.5 h-3.5" /> Task Completed &amp; Approved
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-200 p-12 text-center">
                <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-700">Select a project on the left</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUBMISSION MODAL */}
      {/* ========================================================================= */}
      {showSubmitModal && submittingTask && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-[#f06600]/10 text-[#f06600] rounded-xl">
                  <Send className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    Submit Task Deliverable
                  </h3>
                  <p className="text-xs text-gray-500">
                    Task: <strong className="text-gray-800">{submittingTask.title}</strong>
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowSubmitModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmitWork} className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Submission Mode */}
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
                  Submission Status
                </label>
                <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => setSubmissionData({ ...submissionData, updateType: 'Final' })}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      submissionData.updateType === 'Final'
                        ? 'bg-[#f06600] text-white shadow-xs'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    🏁 Ready for Review (Submit)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSubmissionData({ ...submissionData, updateType: 'Progress' })}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      submissionData.updateType === 'Progress'
                        ? 'bg-white text-gray-900 shadow-xs border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📋 Daily Progress Note
                  </button>
                </div>
              </div>

              {/* GitHub Link */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  GitHub Repository / PR / Figma Link (Optional)
                </label>
                <input
                  type="url"
                  placeholder="https://github.com/organization/repo or Figma link"
                  value={submissionData.githubLink}
                  onChange={e => setSubmissionData({ ...submissionData, githubLink: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                />
              </div>

              {/* Live Link */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  Live Demo / Staging URL / Google Drive (Optional)
                </label>
                <input
                  type="url"
                  placeholder="https://client-demo.vercel.app or Google Drive"
                  value={submissionData.liveLink}
                  onChange={e => setSubmissionData({ ...submissionData, liveLink: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  Work Summary &amp; Deliverable Details *
                </label>
                <textarea
                  required
                  placeholder="Describe the completed work, files changed, or instructions for testing..."
                  value={submissionData.notes}
                  onChange={e => setSubmissionData({ ...submissionData, notes: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600] h-28 resize-none"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  Upload Deliverable Files / Screenshots (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  onChange={e => setFiles(e.target.files)}
                  className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-[#f06600]/10 file:text-[#f06600] hover:file:bg-[#f06600]/20 cursor-pointer"
                />
              </div>

              {/* Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl font-bold text-xs shadow-md shadow-[#f06600]/20 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Deliverable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
